import type { ReactElement } from 'react'
import { NavLink } from 'react-router-dom'
import { cn } from '../../utils/cn'

const icons: Record<string, ReactElement> = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  customers: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.4 2.7-6.2 6-6.2s6 2.8 6 6.2" />
      <circle cx="17.3" cy="9" r="2.3" />
      <path d="M15.6 14.3c2.6.5 4.4 2.7 4.4 5.4" />
    </>
  ),
  apps: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.2" />
      <rect x="14" y="3" width="7" height="7" rx="1.2" />
      <rect x="3" y="14" width="7" height="7" rx="1.2" />
      <rect x="14" y="14" width="7" height="7" rx="1.2" />
      <circle cx="6.5" cy="6.5" r="0.6" fill="currentColor" stroke="none" />
    </>
  ),
  subscriptions: (
    <>
      <path d="M4 12a8 8 0 0 1 14-5.3" />
      <path d="M20 12a8 8 0 0 1-14 5.3" />
      <path d="M18 3v4h-4" />
      <path d="M6 21v-4h4" />
    </>
  ),
  invoices: (
    <>
      <path d="M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v4h4" />
      <path d="M9 12h6M9 16h6" />
    </>
  ),
  support: (
    <path d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4v-4H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
  ),
  cms: (
    <>
      <path d="M12 3l9 5-9 5-9-5 9-5z" />
      <path d="M3 13l9 5 9-5" />
      <path d="M3 17l9 5 9-5" />
    </>
  ),
  messages: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </>
  ),
  sessions: (
    <>
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  services: (
    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2-2z" />
  ),
  migrations: (
    <>
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
      <path d="M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </>
  ),
}

const navItems = [
  { title: 'Dashboard', path: '/admin', icon: 'dashboard', end: true },
  { title: 'Kunden', path: '/admin/customers', icon: 'customers' },
  { title: 'Apps', path: '/admin/apps', icon: 'apps' },
  { title: 'Abonnements', path: '/admin/subscriptions', icon: 'subscriptions' },
  { title: 'Rechnungen', path: '/admin/invoices', icon: 'invoices' },
  { title: 'Support', path: '/admin/support', icon: 'support' },
  { title: 'CMS', path: '/admin/cms', icon: 'cms' },
  { title: 'Kontaktanfragen', path: '/admin/messages', icon: 'messages' },
  { title: 'Sessions', path: '/admin/sessions', icon: 'sessions' },
  { title: 'Services', path: '/admin/services', icon: 'services' },
  { title: 'Migrationen', path: '/admin/migrations', icon: 'migrations' },
  { title: 'Einstellungen', path: '/admin/settings', icon: 'settings' },
]

const filledIcons = new Set(['support', 'services'])

export default function AdminNavigation({ collapsed }: { collapsed: boolean }) {
  return (
    <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {navItems.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.end}
          title={collapsed ? item.title : undefined}
          className={({ isActive }) => cn('wc-sidenav-link', isActive && 'active')}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={filledIcons.has(item.icon) ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            {icons[item.icon]}
          </svg>
          {!collapsed && <span>{item.title}</span>}
        </NavLink>
      ))}
    </nav>
  )
}
